/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { PromisePinData, Ticket, TicketFields } from '@touchplan/common';
import { dateCache } from '@touchplan/common';
import * as _ from 'lodash-es';
import { ClipboardData } from 'src/app/app-data/models/ClipboardData';
import { PlanState } from 'src/app/services/plan-state.service';
import { TicketList } from 'src/app/common/fancy-firebase-ticket/lists/TicketList';
// Disable when not running unit tests, to avoid The Dreaded Circular Dependency
// import { clipboardManagerUnitTests as clipboardManagerUnitTests } from './ClipboardManagerUnitTests';
import * as strings from '@touchplan/strings';
import { baseList } from '@touchplan/common';
import { AbortOrContinue } from './DragAndDropHandler';
import { Point, Bounds, Transform } from 'pixi.js-legacy';
import { EdgeDistance } from 'src/app/app-data/models/DragAtEdge';
import { AbstractApplication } from '@appCanvasCommon/core/AbstractApplication';
import { FancyContainer } from '@appCanvasCommon/graphics/FancyContainer';
import {
	ClipboardPasteDragFeedback,
	CountOfOffScreenTickets,
} from 'src/app/modules/plan/clipboard-drag-lines/ClipboardPasteDragFeedback';
import { PasteMode } from 'src/app/modules/plan/clipboard-in-dock/PasteMode';
import { PLAN_CONSTANTS } from '@touchplan/common';
import { BehaviorSubject, forkJoin, Observable, Subject } from 'rxjs';
import { PlanAccessCheck } from 'src/app/services/PlanAccessCheck';
import { filter, map, take } from 'rxjs/operators';
import { PlanUndosService } from 'src/app/modules/plan/plan-undos.service';
import { ICustomFieldTicket, ICustomFieldTicketList } from 'src/app/app-data/models/CustomFieldTicketValue';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { ITicketAttachmentList } from '../../app-data/models/Attachment';

export interface IClipboardMapData {
	roleId?: { [key: string]: string };
	assignedProjectMemberId?: { [key: string]: string };
	assignedProjectMemberRoleId?: { [key: string]: string };
	creatorId?: { [key: string]: string };
	requestedByProjectMemberId?: { [key: string]: string };
	requestedByProjectMemberRoleId?: { [key: string]: string };
	responsibleProjectMemberId?: { [key: string]: string };
	responsibleProjectMemberRoleId?: { [key: string]: string };
	locationId?: { [key: string]: string };
	priorityId?: { [key: string]: string };
	projectMemberId?: { [key: string]: string };
	procoreRfiId?: { [key: string]: string };
	varianceId?: { [key: string]: string }; //special handling
}

const idMapKeys = [
	'roleId',
	'assignedProjectMemberId',
	'assignedProjectMemberRoleId',
	'creatorId',
	'requestedByProjectMemberId',
	'requestedByProjectMemberRoleId',
	'responsibleProjectMemberId',
	'responsibleProjectMemberRoleId',
	'locationId',
	'priorityId',
	'procoreRfiId',
	// 'isAccepted'
];

const statusKeys = [
	'actualCrewSize',
	'actualDurationDays',
	'actualStart',
	'actualFinish',
	'actualWorkingDays',
	'isActualized',
	'lastPlannedCrewSize',
	'lastPlannedDurationDays',
	'lastPlannedFinish',
	'lastPlannedStart',
	'lastPlannedWorkingDays',
	'promisePeriodStack',
	'promisePeriodPosition',
];

/**
 * The ClipboardManager's "copyTicketOperation$" Observable is just a convenient source of “signals” to indicate
 * when the user selects the “Copy to Clipboard” menu item, and when various paste-related events occur.
 * The values of that Observable are of the following “TicketCopierOperation” "enum" type.
 *
 * Specifically, when a paste aborts because of the “problematic Exact Copy condition”, we need to know.
 * Search for references to the particular "PasteAbortOnActiveLineCross" enumeral.
 * There are other reasons why a paste can abort, so - for the sake of consistency - other
 * TicketCopierOperation enumerals exist for those as well, and might be used in the future.
 */
export enum TicketCopierOperation {
	CopyTickets,
	PasteStart,
	PasteAbortEmptyClipboard,
	PasteAbortOnActiveLineCross,
	PasteAbortReadOnlyAccess,
	PasteAbortOnNoAccessToTickets,
	PasteCompletedSuccessfully,
}

interface INewIdToTicketFieldsMap {
	[newTicketId: string]: /* newTicketFields */ TicketFields;
}
interface IOldIdToNewIdMap {
	[oldTicketId: string]: /* newTicketId */ string;
}

const AUTO_SCROLL_BORDER_DISABLE_RIGHT_THRESHOLD = 130;
const AUTO_SCROLL_BORDER_DISABLE_BOTTOM_THRESHOLD = 130;
const AUTO_SCROLL_BORDER = 100;

const initialEdgeDistances: EdgeDistance = {
	left: AUTO_SCROLL_BORDER,
	top: AUTO_SCROLL_BORDER,
	right: 0,
	bottom: 0,
};
const pasteYOffset = -25; // This makes the mouse "hold" the bottom feedback bar during a paste's drag-n-drop

/**
 * Class that manages copy to and paste from the Clipboard.
 *
 * LIFETIME of the CLIPBOARD.
 * The Clipboard is somewhat unusual as a (singleton) class, in that its singleton is created at application
 * startup as a global and is NOT created when a plan is entered and is NOT destroyed when a plan is exited.
 * That's because the tickets in the Clipboard must be preserved across leaving one plan and entering another.
 * As a result, there are setter methods below which are used to BOTH give the Clipboard Application-level
 * information, as well as (when it changes) Plan-level information.
 */
export class ClipboardManager {
	// Initialized in the constructor, once only
	private clipboardData: ClipboardData;

	// Observable that yields enums about various changes to the clipboard (copy-to, paste-start, kinds of paste-ends)
	public operation$: Subject<TicketCopierOperation>;

	// Convenience filter of the latter that's just the Copy-to-Clipboard (aka CopyTickets) operations
	public copyTicketOperation$: Observable<TicketCopierOperation>;

	// ticket attachments retrieval is async and we need to disable the clipboard while the fetch is in progress to ensure the data integrity
	public ticketAttachmentsCopyInProgress$: BehaviorSubject<boolean>;

	// Observable for when to show the warning about clipboard tickets crossing the Active line
	public showWarningIcon$: Subject<boolean>;

	// These are set at application-creation time, once only
	private _application: AbstractApplication;
	private _dragFeedbackLayer: FancyContainer;

	// These are set when the current plan (in whatever project) changes:
	private _planState: PlanState;
	private _planAccessCheck: PlanAccessCheck;

	// Changes when the clipboard's selected Paste Mode is changed by the user:
	private _currentPasteMode: PasteMode;

	// This is set when the plan's lock changes:
	private _readOnlyAccess = false;

	// These are arrays that are initialized lazily.
	//
	// "baseList" (see import above) is metadata that - among other things - helps map actual ticket fields to planned ticket fields.
	// The parts of the metadata for that actual-to-planned field mapping is saved in "actualKeysToChange", and "actualKeysToChange"
	// is lazily initialized by "getActualKeysToChange()"
	private _actualKeysToChange: string[];
	// Likewise for this array; see "getLastPlannedKeysToNull()"
	private _lastPlannedKeysToNull: string[];

	// These THREE properties are used during an active paste's drag-n-drop:
	// (1) ...
	private _pasteDragFeedback: ClipboardPasteDragFeedback;

	// (2) and (3) ...
	// These flags are used during paste drag-n-drop, to know when the mouse cursor has moved sufficiently out
	// of the clipboard's bottom-right corner, so that the auto-scrolling on the bottom and right can be re-enabled:
	private _clearedBottomThreshold: boolean;
	private _clearedRightThreshold: boolean;

	private _planUndosService: PlanUndosService;
	private _featureFlagManager: FeatureFlagManager;

	constructor() {
		this.clipboardData = new ClipboardData();

		this.operation$ = new Subject<TicketCopierOperation>();
		this.ticketAttachmentsCopyInProgress$ = new BehaviorSubject(false);

		this.copyTicketOperation$ = this.operation$.pipe(
			filter((operation) => operation === TicketCopierOperation.CopyTickets)
		);

		// Update "showWarningIcon$":
		this.showWarningIcon$ = new Subject<boolean>();
		this.copyTicketOperation$.pipe(map((opn) => this.showWarningIcon())).subscribe(this.showWarningIcon$);

		this.currentPasteMode = PasteMode.ExactCopy;
	}

	/* APPLICATION-RELATED SETUP */
	/** This is called once when the application is initialized */
	public onApplicationSetup(application: AbstractApplication, dragFeedbackLayer: FancyContainer) {
		this._application = application;
		this._dragFeedbackLayer = dragFeedbackLayer;
	}

	/* PLAN-RELATED SETUP */
	/* These setters are called when the plan changes (within the same project, or different project) */
	set planState(planState: PlanState) {
		this._planState = planState;
		this.showWarningIcon$.next(this.showWarningIcon());
		// Disable when not running unit tests, to avoid The Dreaded Circular Dependency
		// clipboardManagerUnitTests.planState = planState;
	}
	set planAccessCheck(planAccessCheck: PlanAccessCheck) {
		this._planAccessCheck = planAccessCheck;
	}
	/* PLAN-RELATED METHODS (end) */

	/** Called (say) when the plan is locked or unlcked */
	set readOnlyAccess(readOnlyAccess: boolean) {
		this._readOnlyAccess = readOnlyAccess;
	}

	set planUndosService(planUndosService: PlanUndosService) {
		this._planUndosService = planUndosService;
	}

	set featureFlagManager(featureFlagManager: FeatureFlagManager) {
		this._featureFlagManager = featureFlagManager;
	}

	public handleActiveLineChange() {
		this.showWarningIcon$.next(this.showWarningIcon());
	}

	get currentPasteMode(): PasteMode {
		if (this.ticketsAreActiveSide()) {
			return this._currentPasteMode;
		}
		return PasteMode.TemplateOriginal;
	}

	set currentPasteMode(newMode: PasteMode) {
		if (this._currentPasteMode !== newMode) {
			this._currentPasteMode = newMode;
			this.showWarningIcon$.next(this.showWarningIcon());
		}
	}

	public hasActiveAndPullTickets(ticketMap: Map<string, Ticket>) {
		let hasAnActiveTicket = false;
		let hasAPullTicket = false;
		for (const t of ticketMap.values()) {
			if (t.rawDatabase.isActive) {
				hasAnActiveTicket = true;
				if (hasAPullTicket) {
					return true;
				}
			} else {
				hasAPullTicket = true;
				if (hasAnActiveTicket) {
					return true;
				}
			}
		}
		return false;
	}

	public copySingleTicket(ticket: Map<string, Ticket>): void {
		this.copyTicketList(ticket);
	}

	public copyTickets(): void {
		this.copyTicketList(this._planState.tickets.selectedTicket$.getValue().list);
	}

	public clearTickets(): void {
		if (this.ticketCount() > 0) {
			this.copyTicketList(new Map<string, Ticket>());
		}
	}

	public copyTicketList(ticketList: Map<string, Ticket>): void {
		this.createClipboardData(ticketList, this._planState.projectId, this._planState.planId);
	}

	// this exists solely to create a closure... in case users navigate away from current plan page
	private createClipboardData(ticketList: Map<string, Ticket>, projectId: string, planId: string): void {
		if (ticketList.size > 0 && this._featureFlagManager.getFlag(DynamicFlagTypes.FILE_ATTACH_ENABLED)) {
			this.ticketAttachmentsCopyInProgress$.next(true); // disable the clipboard while fetching the data
			const ticketAttachmentsObs: Array<Observable<ITicketAttachmentList>> = [];
			Array.from(ticketList.keys()).forEach((ticketId) => {
				ticketAttachmentsObs.push(
					this._planState.fileAttachCloneService.getTicketAttachment$(
						this._planState.projectId,
						this._planState.planId,
						ticketId
					)
				);
			});

			forkJoin(ticketAttachmentsObs)
				.pipe(take(1))
				.subscribe((data) => {
					// change data structure from ITicketAttachmentList[] to ITicketAttachmentList to enable easier id lookup
					const obj: ITicketAttachmentList = {};
					for (const item of data) {
						for (const [key, value] of Object.entries(item)) {
							obj[key] = Object.assign(obj[key] || {}, value);
						}
					}
					this.clipboardData = new ClipboardData(
						this.convertMapToTicketArray(ticketList),
						planId,
						projectId,
						this.getCustomFieldTicketValues(ticketList),
						obj
					);
					this.ticketAttachmentsCopyInProgress$.next(false);
					this.operation$.next(TicketCopierOperation.CopyTickets);
				});
		} else {
			this.clipboardData = new ClipboardData(
				this.convertMapToTicketArray(ticketList),
				planId,
				projectId,
				this.getCustomFieldTicketValues(ticketList)
			);
			this.operation$.next(TicketCopierOperation.CopyTickets);
		}
	}

	/**
	 * Returns true if the given mode is not Paste-All, or if it is Paste-All,
	 * and yet nonetheless, all of the tickets in the clipboard can safely be
	 * pasted to the current plan because none of them fall (partially or fully)
	 * into over the Active line and into the pull side.
	 *
	 * If a "problematicExactCopyModeTickets" parameter is provided, and if
	 * the paste node is Paste-All, then any clipboard tickets which do fall
	 * partially or fully over the Active line will be added to that map,
	 * with the ticket ID used at a key into the map.
	 */
	private canPasteAllInExactCopyMode(
		pasteMode: PasteMode,
		problematicExactCopyModeTickets: Map<string, TicketFields> = null
	): boolean {
		if (this.ticketCount() === 0 || pasteMode !== PasteMode.ExactCopy) {
			return true;
		}
		if (!this._planState || !this._planState.plan || !this._planState.plan.syncObject) {
			return true;
		}
		let result = true;
		const activeLineDate = this.activeLineDate();
		this.clipboardData.tickets.forEach((clipboardTicketFields, idx) => {
			const finish = this.maxDateOfTicket(clipboardTicketFields);
			if (finish && finish > activeLineDate) {
				// Pull side
				result = false;
				if (!!problematicExactCopyModeTickets) {
					problematicExactCopyModeTickets.set(clipboardTicketFields.id, clipboardTicketFields);
				}
			}
		});
		return result;
	}

	public canExactCopyTickets(pasteMode: PasteMode): boolean {
		return this.canPasteAllInExactCopyMode(pasteMode, undefined);
	}

	public exactCopyWarningTickets(): Map<string, TicketFields> {
		const activeToPullList: Map<string, TicketFields> = new Map<string, TicketFields>();
		this.canPasteAllInExactCopyMode(PasteMode.ExactCopy, activeToPullList);
		return activeToPullList;
	}

	/** Returns undefined is the clipboard has no "Exact Copy" mode warning tickets */
	public maxClipboardExactCopyWarningTicketsDate(): string {
		return this.maxDateOfTickets(this.exactCopyWarningTickets().values());
	}

	public maxDateOfTickets(tickets: IterableIterator<TicketFields>): string {
		let result: string;
		for (const ticket of tickets) {
			const maxOfTicket = this.maxDateOfTicket(ticket);
			if (maxOfTicket && (!result || result < maxOfTicket)) {
				result = maxOfTicket;
			}
		}
		return result;
	}

	public maxDateOfTicket(ticket: TicketFields): string {
		// Unsure about 'dateRequested', 'dateRequired'
		let maxDate;
		for (const key of ['actualFinish', 'lastPlannedFinish', 'plannedFinish']) {
			const dateString = ticket[key];
			if (dateString && (!maxDate || maxDate < dateString)) {
				maxDate = dateString;
			}
		}
		if (ticket.promisePeriodStack) {
			// tslint:disable-next-line: forin
			for (const key in ticket.promisePeriodStack) {
				const value = ticket.promisePeriodStack[key];
				if (value.actualFinish && (!maxDate || maxDate < value.actualFinish)) {
					maxDate = value.actualFinish;
				}
				if (value.plannedFinish && (!maxDate || maxDate < value.plannedFinish)) {
					maxDate = value.plannedFinish;
				}
			}
		}
		if (ticket.livePromisePeriodStack) {
			for (const pin of ticket.livePromisePeriodStack) {
				if (pin.data) {
					if (pin.data.actualFinish && (!maxDate || maxDate < pin.data.actualFinish)) {
						maxDate = pin.data.actualFinish;
					}
					if (pin.data.plannedFinish && (!maxDate || maxDate < pin.data.plannedFinish)) {
						maxDate = pin.data.plannedFinish;
					}
				}
			}
		}
		return maxDate;
	}

	public pasteTickets(
		pasteMode: PasteMode,
		config: IClipboardMapData,
		dragPoint: Point,
		pastedTicketsOffset: number
	): Bounds {
		if (!config) {
			config = {};
		}

		this.expandProjectMember(config);

		// This will become relevant when we support role, locatin (etc.) mapping "soon"
		const calculatedSrcMappings = this.collectRequiredTicketMappings(this.clipboardData.tickets);

		const ticketsThatWillBePasted: TicketFields[] = [];

		const badTickets = pasteMode === PasteMode.ExactCopy ? this.exactCopyWarningTickets() : null;
		const badTicketCount = !!badTickets ? badTickets.size : 0;

		// Map ticket IDs:
		const newIdToTicketFieldsMap: INewIdToTicketFieldsMap = {};
		const oldToNewIdMap: IOldIdToNewIdMap = {};

		let newTicketsTopY: number;
		let newTicketsBottomY: number;
		let newTicketsLeftMostPullX = Infinity;
		let newTicketsMaxDate: string;

		// --------------------------------------------------------
		// 1-st iteration ...
		//
		// (a) excluding some tickets potentially, either because
		//       - the user doesn't have permission to paste the ticket, or
		//       - in "Exact Copy" mode, the ticket would fall beyond the Active line
		//
		// (b) Clone the remaining tickets
		//
		// (c) Also calculate "newTicketsTopY", "newTicketsBottomY", "newTicketsLeftMostPullX"
		//     and "newTicketsMaxDate".

		let numberOfTicketsExcludedBecauseOfLackOfPermission = 0; // Descriptive variable names, yo.

		this.clipboardData.tickets.forEach((oldTicketsFields) => {
			// If this is a "bad ticket" (one we're excluding because it's Paste-All mode and the ticket
			// wants to be fully or partly on the pull side, but everything needs to be on the active side),
			// then exclude this ticket:
			let thisIsABadTicket = false;
			if (badTicketCount > 0) {
				if (badTickets.get(oldTicketsFields.id)) {
					thisIsABadTicket = true;
				}
			}

			if (!this._planAccessCheck.hasAccessToTicketSync(oldTicketsFields)) {
				numberOfTicketsExcludedBecauseOfLackOfPermission += 1;
				return; // This is a "return" from the "forEach" callback, so think of it as a "continue" statement.
			}

			if (!thisIsABadTicket) {
				// Clone the ticket and give it a new ID:
				const newTicketsFields = this.cloneTicketFields(oldTicketsFields, pasteMode, config);
				ticketsThatWillBePasted.push(newTicketsFields);

				// Update our indexes:
				newIdToTicketFieldsMap[newTicketsFields.id] = newTicketsFields;
				oldToNewIdMap[oldTicketsFields.id] = newTicketsFields.id;

				// Update the X coordinates for the current plan:
				if (newTicketsFields.actualStart) {
					// Active: completed
					newTicketsFields.left = this._planState.activeColumnService.guessXFromDate(
						newTicketsFields.actualStart,
						1
					);
				} else {
					const possiblePlannedStartDate = newTicketsFields.plannedStart || newTicketsFields.lastPlannedStart;
					if (possiblePlannedStartDate) {
						// Active: not completed, but with a planned date
						newTicketsFields.left = this._planState.activeColumnService.guessXFromDate(
							possiblePlannedStartDate,
							1
						);
					}
					/*
						The X/left value for pull tickets isn’t actually important here because the relative locations are fine,
						and the position of pull tickets - as a group - is either basically left-justified against the Active line,
						or - if there’s room - with the user’s cursor “holding” the group of pull tickets. (hand-wave, hand-wave…).
					*/
				}

				if (!newTicketsTopY || newTicketsTopY > newTicketsFields.top) {
					newTicketsTopY = newTicketsFields.top;
				}
				const bottom =
					newTicketsFields.top +
					(newTicketsFields.isActive
						? PLAN_CONSTANTS.ACTIVE_COLUMN_HEIGHT
						: PLAN_CONSTANTS.PULL_TICKET_HEIGHT);
				if (!newTicketsBottomY || newTicketsBottomY < bottom) {
					newTicketsBottomY = bottom;
				}

				if (!newTicketsFields.isActive) {
					// Pull
					if (newTicketsFields.left < newTicketsLeftMostPullX) {
						newTicketsLeftMostPullX = newTicketsFields.left;
					}
				}

				const maxDateOfTicket = this.maxDateOfTicket(newTicketsFields);
				if (maxDateOfTicket && (!newTicketsMaxDate || newTicketsMaxDate < maxDateOfTicket)) {
					newTicketsMaxDate = maxDateOfTicket;
				}
			}
		});
		// End of 1-st iteration
		// --------------------------------------------------------

		const camera = this._planState.canvasConnector.camera;

		let pullXOffset = 0;
		if (newTicketsLeftMostPullX !== Infinity) {
			// There are pull tickets
			const mouseXasReal = camera.screenToRealX(dragPoint.x);
			// This "targetX" basically means: start the X of the paste's drag with the pull tickets either
			// up against the destination plan's Active Line (if the activeLineX is the max), or
			// with the first pull ticket right above the mouse as the drag starts.
			//
			// No really, honest: take my word for it...
			const targetX = Math.max(this.activeLineX(), mouseXasReal - PLAN_CONSTANTS.PULL_TICKET_WIDTH);
			pullXOffset = targetX - newTicketsLeftMostPullX;
		}

		const newTicketsMidY = (newTicketsBottomY + newTicketsTopY) / 2;

		const plansExistingTickets = this._planState.tickets;
		const topY = this.minY(plansExistingTickets, Infinity);
		let yOffset: number;
		let existingTicketsMidY;
		if (topY === Infinity) {
			// There are no existing tickets; empty plan
			const renderer = this._application.renderer;
			const screenHeight = renderer.height / renderer.resolution;
			// Act as if there were a couple of tickets centered on the screen:
			existingTicketsMidY = camera.screenToRealY(screenHeight / 2);
		} else {
			const bottomY = this.maxY(plansExistingTickets, 10000);
			existingTicketsMidY = (bottomY + topY) / 2;
		}
		yOffset = existingTicketsMidY - newTicketsMidY;

		// --------------------------------------------------------
		// 2-nd iteration
		//
		// This pass does two things:
		//
		// (a) If the paste mode is "Planned Data (as planned data)" or "Actual Data (as planned data)",
		//     then adjust the ticket's planned data and actual data fields as needed
		//
		// (b) This pass adjusts the tickets' locations so that the Active line is respected,
		//     and the pasted tickets are generally centered vertically over the existing tickets:
		//
		const ticketsBoundingBox = new Bounds();
		const targetActiveSideX = Math.min(this.activeLineX(), camera.screenToRealX(dragPoint.x));
		ticketsThatWillBePasted.forEach((ticket) => {
			// Adjust for "Planned Data" or "Actual Data" paste mode:
			this.adjustTicketsForPlannedDataOrActualDataMode(pasteMode, ticket, newTicketsMaxDate);

			// Adjust the ticket's location:
			let ticketRight;
			const width = this.ticketWidthInReal(ticket);
			if (ticket.isActive) {
				// Active-side Ticket
				// ------------------
				// We need to know what date to use for positioning.
				// The previous "adjustTicketsForPlannedDataOrActualDataMode" call took the pasteMode into
				// account and if there is still an "actualStart", it means it's Exact Copy mode and that's
				// the date we should use.  Hence it appearing first in the next line of code.  Otherwise
				// in TemplateOriginal or TemplateCurrent, the actual date was either copied to the planned
				// date and nulled, or just nulled; in either case, in those two modes, we use a planned date.
				const ticketStartDate = ticket.actualStart || ticket.plannedStart || ticket.lastPlannedStart;
				if (pasteMode === PasteMode.ExactCopy) {
					// Make sure the left coordinate is based on the date we need to use for the ticket:
					ticket.left = this._planState.activeColumnService.guessXFromDate(ticketStartDate, 1);
				} else {
					// "pasteMode" === TemplateCurrent or TemplateOriginal
					//
					// In these cases, all tickets' locations are offset such that their initial location is
					// to the left of the Active line, but the user can still move them horizontally during
					// drag-n-drop:
					const activeLineDayOffset = dateCache.diffDays(this.activeLineDate(), ticketStartDate) + 1;
					ticket.left = targetActiveSideX - PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH * activeLineDayOffset;
				}
				ticket.height = PLAN_CONSTANTS.ACTIVE_COLUMN_HEIGHT;
				ticket.top = ticket.top + yOffset;
				ticketRight = ticket.left + width;
			} else {
				// Pull-side Ticket
				// ------------------
				ticket.top = ticket.top + yOffset;
				ticket.left = ticket.left + pullXOffset;
				ticket.width = PLAN_CONSTANTS.PULL_TICKET_WIDTH;
				ticketRight = ticket.left + ticket.width;
				ticket.height = PLAN_CONSTANTS.PULL_TICKET_HEIGHT;
			}
			// Account for this ticket in the bounding box of all the tickets:
			ticketsBoundingBox.addFrame(
				Transform.IDENTITY,
				ticket.left,
				ticket.top,
				ticketRight,
				ticket.top + ticket.height
			);
		});
		// End of 2-nd iteration
		// --------------------------------------------------------

		// --------------------------------------------------------
		// 3-rd iteration
		//
		// This final pass does these things:
		//
		// (a) Adjusts the pasted tickets' Y coordinates so their bottom drag handle bar is "attached"
		//     user's mouse pointer/cursor (i.e. "dragPoint.y + pastedTicketsOffset" parameters):
		//
		// (b) "Dependency stuff" to ease adding ticketId swizzling later
		//
		// (c) Most importantly, creates actual tickets in the plan for each of our TicketFields clones
		//
		const screenBottomY = dragPoint.y + pastedTicketsOffset;
		const realDragPointY = camera.screenToRealY(screenBottomY);
		const ticketsBottom = ticketsBoundingBox.maxY;
		const deltaY = realDragPointY - ticketsBottom;
		const newTicketCustomFieldsMap: ICustomFieldTicketList = {};
		const newTicketTicketAttachmentsMap: ITicketAttachmentList = {};

		ticketsThatWillBePasted.forEach((ticket) => {
			ticket.top += deltaY; // Adjust the Y coordinate
			this.dependencyStuff(ticket, newIdToTicketFieldsMap, oldToNewIdMap); // "Dependency stuff"

			// Create the actual ticket in the plan:
			this._planState.actions.createTicketWithId(ticket.id, ticket, undefined, { selectImmediately: true });

			const newToOldIdMap: IOldIdToNewIdMap = {};
			for (const [key, value] of Object.entries(oldToNewIdMap)) {
				newToOldIdMap[value] = key;
			}

			// copy custom fields data
			const customFieldPayload: ICustomFieldTicket =
				this.clipboardData.customFieldTicketValues[newToOldIdMap[ticket.id]];
			this._planState.customFieldsApi.createCustomFieldTicketValues(ticket.id, customFieldPayload);
			newTicketCustomFieldsMap[ticket.id] = customFieldPayload;

			// copy file attach data
			if (
				pasteMode === PasteMode.ExactCopy &&
				this._featureFlagManager.getFlag(DynamicFlagTypes.FILE_ATTACH_ENABLED)
			) {
				const attachments = this.clipboardData.ticketAttachments[newToOldIdMap[ticket.id]];
				this._planState.fileAttachCloneService.copyAttachment$(
					this._planState.projectId,
					this._planState.planId,
					ticket.id,
					attachments
				);
				newTicketTicketAttachmentsMap[ticket.id] = attachments;
			}

			//TP-655 TODO - debug if this will actually work...
			// this is the first step of the currently available "add but don't persist api"
			// const action = this._planState.actions.addFloatingTicketToPlan(ticket);
			// this._planState.actions.selectTicket(action.payload);
		});

		// create clipboard paste undo
		this._planUndosService.clipboardPasteOperation.snapshotUndoState(
			Array.from(this._planState.tickets.selectedTicket$.getValue().list.values()),
			newTicketCustomFieldsMap,
			newTicketTicketAttachmentsMap
		);

		// End of 3-rd iteration
		// --------------------------------------------------------

		// Warn the user about tickets excluded because of lack of permission:
		if (numberOfTicketsExcludedBecauseOfLackOfPermission > 0) {
			this.displaySomeNotPastedAccessRestrictionsWarning(
				numberOfTicketsExcludedBecauseOfLackOfPermission,
				ticketsThatWillBePasted.length
			);
		}

		// Warn the user about tickets excluded / to-the-right-of the Active line:
		if (badTicketCount > 0) {
			this.displayExactCopyWarning();
		}

		if (ticketsThatWillBePasted.length === 0) {
			return null;
		}
		return ticketsBoundingBox;
	}

	// If the paste mode is:
	//     - Actual Data (as planned data)
	// then completed tickets, which have "actual" dates, need to use those actual dates
	// to overwrite the "planned" data and otherwise null-out the actual fields.
	//
	// If the paste mode is:
	//     - Planned Data (as planned data)
	// then completed tickets, which have "actual" dates, need to use the existing planned data
	// and otherwise just null-out the actual fields
	//
	// The pre-calculated maximum date over all of the tickets is passed as a parameter
	// because in Planned and Actual data modes, all of the dates initially given to tickets
	// (which are "as planned data") should be before the Active line if the tickets are
	// active and not pull side.  So knowing the max date lets all dates be adjusted uniformly,
	// so that all active tickets have start AND end active-side dates.
	//
	private adjustTicketsForPlannedDataOrActualDataMode(
		mode: PasteMode,
		ticket: TicketFields,
		maxDate: string
	): void {
		if (mode === PasteMode.TemplateOriginal || mode === PasteMode.TemplateCurrent) {
			const activeDate = this.activeLineDate();
			const negativeDayCountToAdd =
				maxDate && maxDate > activeDate ? dateCache.diffDays(activeDate, maxDate) : 0;

			// The metadata for the actual-to-planned field mapping:
			const actualKeysToChange = this.getActualKeysToChange();

			const ticketHasActualStart = !!ticket.actualStart;
			// For each ticket with actual data...
			if (ticket.isActive) {
				// ... change the actual and planned fields as needed:
				for (const actualKey of actualKeysToChange) {
					const plannedKey: string = baseList[actualKey].plannedKey;
					const keyOfFieldToConvert =
						mode === PasteMode.TemplateCurrent && ticketHasActualStart ? actualKey : plannedKey;
					const fieldValueToConvert = ticket[keyOfFieldToConvert];
					let newValue;
					if (fieldValueToConvert && baseList[actualKey].type === 'date') {
						newValue = dateCache.addDays(fieldValueToConvert, negativeDayCountToAdd);
					} else {
						newValue = fieldValueToConvert;
					}
					if (newValue !== undefined) {
						// Ticket validation doesn't like (e.g.) an "undefined" durationDays
						ticket[plannedKey] = newValue;
					}
					ticket[actualKey] = null;
				}

				// TP-794
				// The ticket must have a valid "plannedStart".
				// In r36.5, both actualStart and plannedStart might not be set.
				// In such a case, plannedStart at this point in the code may still not be set.
				// However, actualStartInferred may have a valid value, so check for that:
				if (ticket.actualStartInferred && !ticket.plannedStart) {
					ticket.plannedStart = ticket.actualStartInferred;
				}

				// Now null certain fields, based on the "baseList" metadata:
				const keysToNull = this.getLastPlannedKeysToNull();
				for (const key of keysToNull) {
					ticket[key] = null;
				}
				ticket.actualStartInferred = null;
				ticket.promisePeriodPosition = null;
				ticket.promisePeriodStack = null;
			}
		}
	}

	private getActualKeysToChange() {
		if (!this._actualKeysToChange) {
			// "baseList" is metadata that - among other things - helps map actual ticket fields to planned ticket fields.
			// Find the parts of the metadata for that actual-to-planned field mapping and save that metadata in "actualKeysToChange":
			this._actualKeysToChange = [];
			for (const baseListKey in baseList) {
				if (!!baseList[baseListKey].plannedKey) {
					this._actualKeysToChange.unshift(baseListKey);
				}
			}
		}
		return this._actualKeysToChange;
	}

	private getLastPlannedKeysToNull() {
		if (!this._lastPlannedKeysToNull) {
			// "baseList" is metadata that - among other things - helps null certain actual ticket fields.
			// Find the parts of the metadata for those fields:
			this._lastPlannedKeysToNull = [];
			// tslint:disable-next-line: forin
			for (const baseListKey in baseList) {
				const lastPlannedKey = baseList[baseListKey].lastPlannedKey;
				if (!!lastPlannedKey) {
					this._lastPlannedKeysToNull.unshift(lastPlannedKey);
				}
			}
		}
		return this._lastPlannedKeysToNull;
	}

	public dragStart(event: MouseEvent, planState: PlanState): AbortOrContinue {
		const selectedMode = this.currentPasteMode;
		if (this.ticketCount() === 0) {
			this.operation$.next(TicketCopierOperation.PasteAbortEmptyClipboard);
			return AbortOrContinue.Abort;
		}
		if (this._readOnlyAccess) {
			Logging.warning('Read-only:  cannot paste from clipboard: read-only.');
			this.operation$.next(TicketCopierOperation.PasteAbortReadOnlyAccess);
			return AbortOrContinue.Abort;
		}
		if (this.showWarningIcon()) {
			this.operation$.next(TicketCopierOperation.PasteAbortOnActiveLineCross);
			return AbortOrContinue.Abort;
		}
		const dragPoint = new Point(event.x, event.y);

		const ticketList = planState.tickets.selectedTicket$.getValue().list;
		if (ticketList && ticketList.size > 0) {
			planState.actions.clearSelection(ticketList);
		}

		this._clearedBottomThreshold = false;
		this._clearedRightThreshold = false;

		const ticketsBoundingBox = this.pasteTickets(selectedMode, undefined, dragPoint, pasteYOffset);

		// If no bounding box, then access restrictions prevented all of the clipboard's tickets
		// from being paste-able:
		if (ticketsBoundingBox === null) {
			this.operation$.next(TicketCopierOperation.PasteAbortOnNoAccessToTickets);
			return AbortOrContinue.Abort;
		}
		const dragConfig = {
			dragAtEdgeDistance: initialEdgeDistances,
			forceVerticalOnly: selectedMode === PasteMode.ExactCopy,
			isUndoable: false,
		};
		planState.ticketDragService.dragStart(dragPoint, planState.tickets.selectedTicket$, dragConfig);

		const camera = this._planState.canvasConnector.camera;
		const ticketsMinY = camera.realToScreenY(ticketsBoundingBox.minY);
		const ticketsMaxY = camera.realToScreenY(ticketsBoundingBox.maxY);

		this._pasteDragFeedback = new ClipboardPasteDragFeedback(this._application, this._dragFeedbackLayer);

		const boundsHeight = ticketsMaxY - ticketsMinY + 1;
		const ticketsOffScreen = this.getCountOfOffScreenTickets(
			planState.tickets.selectedTicket$.getValue().list
		);
		this._pasteDragFeedback.initializeLineData(dragPoint.y + pasteYOffset, boundsHeight, ticketsOffScreen);
		this._pasteDragFeedback.setVisible(true);

		// For some reason, without this, a magnified version of the drag-feedback initially appears
		// if you just press-and-hold when starting the drag-n-drop from the clipboard.
		// So this is an unfortunate work-around...  :-(
		this._pasteDragFeedback.initializeLineData(dragPoint.y + pasteYOffset, boundsHeight, ticketsOffScreen);

		return AbortOrContinue.Continue;
	}

	public drag(event: MouseEvent, planState: PlanState): AbortOrContinue {
		const dragPoint = new Point(event.x, event.y);

		this.considerUpdatingEdgeDistances(dragPoint, planState);

		const ticketsOffScreen = this.getCountOfOffScreenTickets(
			planState.tickets.selectedTicket$.getValue().list
		);
		this._pasteDragFeedback.updateLineData(dragPoint.y + pasteYOffset, ticketsOffScreen);

		planState.ticketDragService.drag(dragPoint);
		return AbortOrContinue.Continue;
	}

	public dragEnd(event: MouseEvent, planState: PlanState): void {
		const dragPoint = new Point(event.x, event.y);

		this.considerUpdatingEdgeDistances(dragPoint, planState, true);
		this._pasteDragFeedback.setVisible(false);
		this._pasteDragFeedback.destroy();

		planState.ticketDragService.dragEnd(dragPoint);

		this.operation$.next(TicketCopierOperation.PasteCompletedSuccessfully);
	}

	protected getCountOfOffScreenTickets(tickets: Map<string, Ticket>): CountOfOffScreenTickets {
		let left = 0;
		let right = 0;
		const screenWidth = this.getScreenWidth();
		const camera = this._planState.canvasConnector.camera;
		tickets.forEach((ticket: Ticket) => {
			const ticketsScreenLeft = camera.realToScreenX(ticket.drag.left);
			// Rough estimate, admittedly:
			const width = this.ticketWidthInReal(ticket.rawDatabase);
			const ticketsScreenRight = camera.realToScreenX(ticket.drag.left + width);

			// Check the left and right screen thresholds:
			if (ticketsScreenRight < PLAN_CONSTANTS.SIDEBAR_WIDTH) {
				left += 1;
			} else if (ticketsScreenLeft > screenWidth) {
				right += 1;
			}
		});
		return { left, right };
	}

	private ticketWidthInReal(ticket: TicketFields): number {
		if (ticket.isActive) {
			const dayCount = ticket.actualStart ? ticket.actualDurationDays : ticket.durationDays;
			return PLAN_CONSTANTS.ACTIVE_COLUMN_WIDTH * dayCount;
		}
		// Pull
		return PLAN_CONSTANTS.PULL_TICKET_WIDTH;
	}

	private getScreenWidth(): number {
		const renderer = this._application.renderer;
		return renderer.width / renderer.resolution;
	}

	/**
	 * The auto-scroll that normally happens when you drag tickets and get near the edge needs to be disabled initially
	 * when you START the drag-n-drop from the Clipboard, given the Clipboard is already at the bottom-right edge of the screen.
	 * So this function checks to see if the user has moved the mouse inside from the bottom-right enough to remember that
	 * the right and/or bottom "threshold" has been cleared, in which case the corresponding edge's auto-scroll will be
	 * re-enabled.
	 */
	public considerUpdatingEdgeDistances(dragPoint: Point, planState: PlanState, force: boolean = false): void {
		if (
			!this._clearedRightThreshold &&
			(force || dragPoint.x < window.innerWidth - AUTO_SCROLL_BORDER_DISABLE_RIGHT_THRESHOLD)
		) {
			this._clearedRightThreshold = true;
			planState.ticketDragService.updateDragAtEdgeDistance('right', AUTO_SCROLL_BORDER);
		}
		if (
			!this._clearedBottomThreshold &&
			(force || dragPoint.y < window.innerHeight - AUTO_SCROLL_BORDER_DISABLE_BOTTOM_THRESHOLD)
		) {
			this._clearedBottomThreshold = true;
			planState.ticketDragService.updateDragAtEdgeDistance('bottom', AUTO_SCROLL_BORDER);
			planState.scopeSoup.floatingRegionApi.hide();
		}
	}

	private cloneTicketFields(ticket: TicketFields, pasteMode: PasteMode, config: IClipboardMapData) {
		// Clone the ticket and give it a new ID:
		const newTicketsFields = _.cloneDeep(ticket) as TicketFields;

		newTicketsFields.id = this._planState.tickets.listRef.query.ref.push().key;

		/*
				This maps fields in the new clone as required by the "config" map data.
				For instance, say "config" included:

						roleId: {
							-fakeId_A: "placeholder--fakeId_A",
							-fakeId_B: "placeholder--fakeId_B"
						}

				and say the copied "roleId" is "-fakeId_A".
				The when "key" is "roleId":

						newTicketsFields[key] = newTicketsFields.roleId = "-fakeId_A"
						config[key][newTicketsFields[key]] = config.roleId["-fakeId_A"] = "placeholder--fakeId_A"

				so that means the clone's roleId is assigned the mapped value:

						ewTicketsFields.roleId = "placeholder--fakeId_A"
			*/
		for (const key in config) {
			if (newTicketsFields[key] && (config as any)[key][newTicketsFields[key]]) {
				newTicketsFields[key] = (config as any)[key][newTicketsFields[key]];
			}
		}

		/* Same kinda deal */
		this.iterVariance(newTicketsFields, (varianceId: string, pp: PromisePinData) => {
			const key = 'varianceId';
			if (config[key] && config[key][varianceId]) {
				pp.varianceId = config[key][varianceId];
			}
		});
		return newTicketsFields;
	}

	private dependencyStuff(
		ticket: TicketFields,
		newIdToTicketFieldsMap: INewIdToTicketFieldsMap,
		oldIdToNewIdMap: IOldIdToNewIdMap
	) {
		if (!ticket.predecessors || this.clipboardData.ticketCount() === 0) {
			return;
		}

		// strip other plans
		if (ticket.predecessors[this.clipboardData.planId]) {
			const planId = this._planState.planId;
			ticket.predecessors = { [planId]: ticket.predecessors[this.clipboardData.planId] };

			const ticketSection = ticket.predecessors[planId];

			// now strip tickets that don't exist
			const keepers: any = {};
			let anyKeepers = false;
			// tslint:disable-next-line: forin
			for (const tDep in ticketSection) {
				const newIdOfPredecessorTicketFields = oldIdToNewIdMap[tDep];
				// re-map here
				if (newIdToTicketFieldsMap[newIdOfPredecessorTicketFields] !== undefined) {
					keepers[newIdOfPredecessorTicketFields] = ticketSection[tDep];
					anyKeepers = true;
				}
			}

			ticket.predecessors[planId] = keepers; // Replace planId
			if (!anyKeepers) {
				delete ticket.predecessors;
			}
		} else {
			delete ticket.predecessors;
		}
	}

	private minY(tickets: TicketList, defaultValue: number) {
		let result = Infinity;
		tickets.syncList.forEach((t) => {
			if (t.database.bottom < result) {
				result = t.database.bottom;
			}
		});
		return result === Infinity ? defaultValue : result;
	}

	private maxY(tickets: TicketList, defaultValue: number) {
		let result = defaultValue;
		tickets.syncList.forEach((t) => {
			if (t.database.top > result) {
				result = t.database.top;
			}
		});
		return result;
	}

	private maxX(tickets: TicketList, defaultValue: number) {
		let result = defaultValue;
		tickets.syncList.forEach((t) => {
			if (t.database.left > result) {
				result = t.database.left;
			}
		});
		return result;
	}

	private activeLineX() {
		return this._planState.plan.syncObject.view.activeLineX;
	}

	private activeLineDate() {
		return this._planState.plan.syncObject.view.activeLineDate;
	}

	private convertMapToTicketArray(ticketMap: Map<string, Ticket>): TicketFields[] {
		const ticketArray = Array.from(ticketMap.values());
		if (ticketArray && ticketArray[0] && ticketArray[0].$id !== undefined) {
			return ticketArray.map((t) => {
				const clonedTicketFields: TicketFields = { ...t.rawDatabase }; //avoid reference issues when the exception days get added
				const exceptionDays = t.database.ticketExceptionDays ? new Map(t.database.ticketExceptionDays) : null; //same here
				if (exceptionDays) {
					clonedTicketFields.ticketExceptionDays = exceptionDays;
				}
				return clonedTicketFields;
			});
		}
		return ticketArray;
	}

	private getCustomFieldTicketValues(ticketMap: Map<string, Ticket>) {
		const data: Record<string, ICustomFieldTicket> = {};
		ticketMap.forEach((value, key) => {
			data[key] = this._planState.customFieldsApi.cloneCustomFieldTicketValues(key);
		});
		return data;
	}

	//also roles now
	public expandProjectMember(config: IClipboardMapData) {
		if (config && config.projectMemberId) {
			config.assignedProjectMemberId = config.projectMemberId;
			config.requestedByProjectMemberId = config.projectMemberId;
			config.responsibleProjectMemberId = config.projectMemberId;
			config.creatorId = config.projectMemberId;

			config.assignedProjectMemberRoleId = config.roleId;
			config.responsibleProjectMemberRoleId = config.roleId;
			config.requestedByProjectMemberRoleId = config.roleId;
		}
		return config;
	}
	public joinProjectMember(config: IClipboardMapData) {
		if (!config) {
			return config;
		}
		//projectMembers
		config.projectMemberId = {
			...(config.assignedProjectMemberId || {}),
			...(config.requestedByProjectMemberId || {}),
			...(config.responsibleProjectMemberId || {}),
			...(config.creatorId || {}),
		};
		delete config.assignedProjectMemberId;
		delete config.requestedByProjectMemberId;
		delete config.responsibleProjectMemberId;
		delete config.creatorId;

		//roles
		config.roleId = {
			...(config.roleId || {}),
			...(config.assignedProjectMemberRoleId || {}),
			...(config.responsibleProjectMemberRoleId || {}),
			...(config.requestedByProjectMemberRoleId || {}),
		};
		delete config.assignedProjectMemberRoleId;
		delete config.responsibleProjectMemberRoleId;
		delete config.requestedByProjectMemberRoleId;

		return config;
	}

	/*
		Returns something like (this example doesn't include "ticket.promisePeriodStack" stuff):
		{
			roleId: {
				-fakeId_A: "placeholder--fakeId_A",
				-fakeId_B: "placeholder--fakeId_B"
			}
			creatorId: {-fakeId_C: "placeholder--fakeId_C"}
			requestedByProjectMemberId: {-fakeId_C: "placeholder--fakeId_C"}
			requestedByProjectMemberRoleId: {-fakeId_B: "placeholder--fakeId_B"}
			responsibleProjectMemberId: {-fakeId_C: "placeholder--fakeId_C"}
			responsibleProjectMemberRoleId: {-fakeId_B: "placeholder--fakeId_B"}
			locationId: {-fakeId_D: "placeholder--fakeId_D", -fakeId_E: "placeholder--fakeId_E"}
		}

		Note that when the same "fake_X" ID matches, that's intentional and reflects what
		this function does.
	*/
	public collectRequiredTicketMappings(ticketList: TicketFields[]) {
		const collection: IClipboardMapData = {};
		ticketList.forEach((ticket) => {
			idMapKeys.forEach((key) => {
				if (!ticket[key]) {
					return;
				}
				if (!(collection as any)[key]) {
					(collection as any)[key] = {};
				}
				(collection as any)[key][ticket[key]] = 'placeholder-' + ticket[key];
			});

			// variance
			this.iterVariance(ticket, (varianceId: string, pp: PromisePinData) => {
				const key = 'varianceId';
				if (!collection[key]) {
					collection[key] = {};
				}
				collection[key][varianceId] = 'placeholder-' + varianceId;
			});
		});
		return collection;
	}

	private iterVariance(ticket: TicketFields, cb: (varianceId: string, pp: PromisePinData) => void) {
		if (ticket.promisePeriodStack) {
			for (const ppId in ticket.promisePeriodStack) {
				if (ticket.promisePeriodStack[ppId] && ticket.promisePeriodStack[ppId].varianceId) {
					cb(ticket.promisePeriodStack[ppId].varianceId, ticket.promisePeriodStack[ppId]);
				}
			}
		}
	}

	public showWarningIcon(): boolean {
		if (
			this.ticketCount() === 0 ||
			this.ticketsArePullSide() ||
			this.currentPasteMode !== PasteMode.ExactCopy
		) {
			return false;
		}
		// So here we know the clipboard isn't empty, has active tickets, and the paste-mode is Paste-All:
		return !this.canExactCopyTickets(this.currentPasteMode);
	}

	private displayExactCopyWarning() {
		const badTickets = this.exactCopyWarningTickets();
		const badCount = badTickets.size;
		const maxClipboardDate = dateCache.getFormat(this.maxDateOfTickets(badTickets.values()), 'MMM D, YYYY');
		const text = strings.WARNING_ACTIVE_TICKETS_AFTER_ACTIVE_LINE_DATE_WERE_NOT_PASTED(
			badCount,
			this.ticketCount(),
			maxClipboardDate
		);
		Logging.warning(text);
	}

	private displaySomeNotPastedAccessRestrictionsWarning(
		excludedCount: number,
		ticketsToBePastedCount: number
	) {
		let text;
		if (ticketsToBePastedCount === 0) {
			text = strings.WARNING_NO_TICKETS_PASTED_BECAUSE_OF_ACCESS_RESTRICTIONS;
		} else {
			const totalCount = excludedCount + ticketsToBePastedCount;
			text = strings.WARNING_SOME_TICKETS_PASTED_EXCLUDED_OF_ACCESS_RESTRICTIONS(excludedCount, totalCount);
		}
		Logging.warning(text);
	}

	public stripStatus() {
		this.clipboardData.tickets.forEach((ticket) => {
			this.stripTicketStatus(ticket);
		});
	}

	private stripTicketStatus(ticket: TicketFields) {
		if (!ticket.plannedStart && ticket.lastPlannedStart) {
			ticket.plannedStart = ticket.lastPlannedStart;
		}
		if (!ticket.plannedFinish && ticket.lastPlannedFinish) {
			ticket.plannedFinish = ticket.lastPlannedFinish;
		}
		statusKeys.forEach((key) => {
			delete ticket[key];
		});
	}

	// *************************************************
	//  Methods that just delegate to the ClipboardData
	// *************************************************

	public tickets() {
		return this.clipboardData.tickets;
	}
	public ticketCount() {
		return this.clipboardData.ticketCount();
	}
	public ticketsProjectId() {
		return this.clipboardData.projectId;
	}
	public ticketCountString() {
		return this._readOnlyAccess ? '' : this.clipboardData.ticketCountString();
	}
	public ticketsAreActiveSide() {
		return this.clipboardData.ticketsAreActiveSide();
	}
	public ticketsArePullSide() {
		return !this.ticketsAreActiveSide();
	}
}

export const clipboardManager = new ClipboardManager();
window.clipboardManager = clipboardManager;
